const { fetchJSON } = window.BAO.Utils

class StoreSwitcher extends window.BAO.CustomElement() {
  connectedCallback () {
    this.form =
      this.querySelector('#MarketSwitcherForm') ||
      this.querySelector('form.drw-MarketSwitcher_Form')
    this.select = this.querySelector('[name="country_code"]')
    this._countries = this.loadCountries()

    super.connectedCallback()

    if (window.location.href.indexOf('#geo') > -1) {
      return this.forceRedirect()
    }

    if (this.countryParam) {
      this.syncStoredCountry(this.countryParam)

      const url = new URL(window.location.href)
      url.searchParams.delete('country')
      window.history.replaceState({}, '', url.toString())
    }

    const storedCode = window.localStorage.getItem(this.userLocationName)
    const storedSubfolder = storedCode
      ? this.countries[storedCode.toUpperCase()]?.subfolder
      : null
    const currentSubfolder = this.marketCountry
      ? this.countries[this.marketCountry]?.subfolder
      : null

    if (this.marketCountry && storedSubfolder !== currentSubfolder) {
      window.localStorage.setItem(this.userLocationName, this.marketCountry)
    } else if (!this.noLocationStored) {
      this.syncStoredCountry(storedCode)
    } else if (this.marketCountry) {
      window.localStorage.setItem(this.userLocationName, this.marketCountry)
    }

    Object.entries(this.countries).forEach(([key]) => {
      const isStoredCountry = key.toLowerCase() === storedCode?.toLowerCase()
      if (!isStoredCountry) return

      const { site, subfolder } = this.countries[key]
      this.storedUrl = this.createCountryAwareHref(key, site, subfolder)
    })

    if (this.storedUrl) {
      const storedCountry = this.countries[storedCode?.toUpperCase()]
      const storedSite = storedCountry?.site?.trim()
      const currentStore = this.dataset.store?.trim()

      // Only auto-redirect within the same store (market path). Do not bounce
      // visitors off the AUS shop when their saved preference is a UK market.
      if (storedSite !== currentStore) return

      const currentUrl = new URL(window.location.href)
      const storedUrl = new URL(this.storedUrl)

      currentUrl.search = ''
      storedUrl.search = ''

      if (currentUrl.href !== storedUrl.href) {
        window.location.href = storedUrl.href
      }
    }
  }

  forceRedirect () {
    fetchJSON('/browsing_context_suggestions.json', {
      method: 'GET',
    }).then((res) => {
      const userCountry = res.detected_values.country.handle.toUpperCase()
      const { site, subfolder } = this.countries[userCountry]
      const subfolderSegment = subfolder ? `/${subfolder}` : ''
      window.location.href = `${this.selectedSites[site].url}${subfolderSegment}${this.cleanPageUrl}`
    })
  }

  setupListeners () {
    super.setupListeners()

    this.onFormSubmit = this.onFormSubmit.bind(this)

    if (this.form) {
      this.listeners.add(this.form, 'submit', this.onFormSubmit, { capture: true })
    }
  }

  onFormSubmit (event) {
    if (!this.select) return

    const code = this.select.value?.trim()?.toUpperCase()
    const country = this.countries[code]

    if (!country) return

    const site = country.site?.trim()
    const subfolder = country.subfolder?.trim()
    const href = this.createCountryAwareHref(code, site, subfolder)
    const isCrossStore = site !== this.dataset.store?.trim()

    let targetUrl

    try {
      targetUrl = new URL(href)
    } catch {
      return
    }

    const currentUrl = new URL(window.location.href)
    const isSameDestination =
      targetUrl.origin === currentUrl.origin &&
      targetUrl.pathname === currentUrl.pathname &&
      targetUrl.search === currentUrl.search

    if (!isCrossStore && isSameDestination) return

    event.preventDefault()
    event.stopPropagation()

    window.localStorage.setItem(this.userLocationName, code)
    window.location.assign(href)
  }

  loadCountries () {
    const configEl = this.querySelector('[data-store-switcher-el="countries"]')

    if (configEl?.textContent) {
      try {
        const parsed = JSON.parse(configEl.textContent)

        if (parsed && typeof parsed === 'object') {
          window.theme ??= {}
          window.theme.countries = parsed
          return parsed
        }
      } catch {
        /* use window.theme.countries fallback */
      }
    }

    return window.theme?.countries ?? {}
  }

  syncStoredCountry (countryCode) {
    const normalizedCountryCode = countryCode.toUpperCase()
    if (this.countries[normalizedCountryCode]?.site === this.dataset.store) {
      window.localStorage.setItem(this.userLocationName, normalizedCountryCode)
    }
  }

  createCountryAwareHref (countryCode, site, subfolder) {
    const siteUrl = this.selectedSites[site]?.url?.replace(/\/$/, '')
    if (!siteUrl) return window.location.href

    const pagePath = this.cleanPageUrl.startsWith('/')
      ? this.cleanPageUrl
      : `/${this.cleanPageUrl}`
    const subfolderSegment = subfolder ? `/${subfolder}` : ''
    const targetUrl = new URL(`${siteUrl}${subfolderSegment}${pagePath}`)

    if (targetUrl.origin !== window.location.origin) {
      targetUrl.searchParams.set('country', countryCode)
    }

    return targetUrl.toString()
  }

  get cleanPageUrl () {
    const pageUrl = this.dataset.pageUrl || '/'
    const subfolders = Object.values(this.countries)
      .map((c) => c.subfolder?.trim())
      .filter(Boolean)

    for (const subfolder of subfolders) {
      if (pageUrl.startsWith(`/${subfolder}/`)) {
        return pageUrl.slice(`/${subfolder}`.length)
      }
    }

    return pageUrl
  }

  get countryParam () {
    const countryCode = new URLSearchParams(window.location.search).get(
      'country',
    )
    if (!countryCode) return null

    const normalizedCountryCode = countryCode.toUpperCase()
    return this.countries[normalizedCountryCode] ? normalizedCountryCode : null
  }

  get noLocationStored () {
    return (
      window.localStorage.getItem(this.userLocationName) == null ||
      (window.localStorage.getItem(this.userLocationName) === undefined &&
        !window.Shopify.designMode)
    )
  }

  get userLocationName () {
    return 'STRIKE-BAO-localization'
  }

  get selectedSites () {
    return {
      INT: { url: this.dataset.intUrl },
      AU: { url: this.dataset.auUrl },
    }
  }

  get marketCountry () {
    const code = this.dataset.currentCountry?.toUpperCase()
    if (!code || !this.countries[code]) return null
    return this.countries[code].subfolder ? code : null
  }

  get countries () {
    return this._countries
  }
}

if (!customElements.get('store-switcher')) {
  customElements.define('store-switcher', StoreSwitcher)
}
